// Yale University: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - Yale's Common Data Set 2025-26 (yale_cds_2025-26_md_20260616.pdf from
//    oir.yale.edu): C1 applicants/admits/enrollees for Fall 2025; C8 test
//    policy; C21/C22 early plans; G1/G5 2026-2027 costs. C1 gives men and
//    women with no total row and no other category, so applied, admitted and
//    enrolled are those two rows added. The residency table is blank. G1
//    leaves required fees blank, so there is no fees row.
//  - admissions.yale.edu (timelines, standardized testing, requirements).
//    Quirk: the CDS C8 still describes the older "test-flexible" policy
//    (ACT, SAT, AP or IB) in a table headed "Fall 2025"; Yale's admissions
//    site now says first-year applicants are required to include ACT or SAT
//    scores. The site wins for fall 2027; the difference is stated on the page.
//  - College Scorecard, school id 130794 (net price by family income): the
//    API response the collegescorecard.ed.gov school page loads (the
//    DEMO_KEY call was rate-limited).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Yale Common Data Set 2025-2026',
        url: 'https://oir.yale.edu/sites/default/files/yale_cds_2025-26_md_20260616.pdf',
        read: '2026-09-30',
    },
    timelines: {
        label: 'Yale Undergraduate Admissions, Timelines',
        url: 'https://admissions.yale.edu/timelines',
        read: '2026-09-30',
    },
    tests: {
        label: 'Yale Undergraduate Admissions, Standardized Testing',
        url: 'https://admissions.yale.edu/standardized-testing',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?130794-Yale-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'yale-university',
    kbId: 'yale_university',
    name: 'Yale University',
    shortName: 'Yale',
    location: 'New Haven, CT',
    control: 'Private',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (Yale identity guidelines); decoration only.
    branding: {
        primary: '#00356B',
        secondary: '#978D85',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/yale_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 50264,
        admitted: 2387,
        enrolled: 1633,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Yale\'s Common Data Set reports one first-year acceptance rate for Yale College, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Single-choice early action',
        note: 'Yale has no early decision plan, and the Common Data Set does not report how many applied or were admitted in early action, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 2' },
        rows: [
            { plan: 'Single-Choice Early Action', deadline: 'November 1', decision: 'Mid-December', binding: false },
            { plan: 'QuestBridge National College Match', deadline: 'November 1', decision: 'Early December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 2', decision: 'Late March', binding: false },
        ],
        note: 'Admitted students reply by May 1. The QuestBridge Match is binding: if you match with Yale, you withdraw your other applications.',
        source: 'timelines',
        binding: 'Single-choice early action is not binding, and if admitted you may keep other applications active. While you apply this way, you may not apply early action or early decision to other colleges, except nonbinding rolling programs, nonbinding early programs at public institutions, early programs that notify after January 1, and institutions outside the United States. If you are denied in early action, you cannot apply again in regular decision that year.',
        bindingSource: 'timelines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Yale requires first-year applicants to include ACT or SAT scores. AP or IB scores may be added but are not required. You self-report scores on the application and send official scores if you enroll.',
        source: 'tests',
        cdsLine: 'The Common Data Set still describes Yale\'s older test-flexible policy, where you chose among ACT, SAT, AP and IB scores, in its testing section for fall 2025. Yale\'s admissions site now asks for the ACT or SAT.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'timelines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 72500 },
            { item: 'Food and housing (on campus)', amount: 21600 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Transportation', amount: 1100 },
            { item: 'Other expenses', amount: 2700 },
        ],
        total: 98900,
        note: 'The Common Data Set lists no separate required fees.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 11340,
            byIncome: [
                { income: '$0 to $30,000', amount: 1256 },
                { income: '$30,001 to $48,000', amount: 867 },
                { income: '$48,001 to $75,000', amount: 854 },
                { income: '$75,001 to $110,000', amount: 7828 },
                { income: 'Over $110,000', amount: 47749 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Yale\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
